import {
  startOfMonth,
  endOfMonth,
  subMonths,
  format,
} from 'date-fns'

export type DateRangeType = 'lastMonth' | 'lastQuarter' | 'lastFY' | 'ytd' | 'last12'

export type GroupBy = 'day' | 'month'

export interface DateRangeResult {
  start: string
  end: string
  groupBy: GroupBy
}

/** Current financial year (FY) in AU style: 1 Mar – 28/29 Feb. Returns { start, end } as Dates. */
function getCurrentFY(today: Date): { start: Date; end: Date } {
  const y = today.getFullYear()
  const m = today.getMonth()
  const fyEndYear = m < 2 ? y : y + 1
  const fyStartYear = fyEndYear - 1
  const lastDay = new Date(fyEndYear, 2, 0).getDate()
  return {
    start: new Date(fyStartYear, 2, 1),
    end: new Date(fyEndYear, 1, lastDay),
  }
}

/**
 * Returns start/end ISO date strings and groupBy for snapshot metrics.
 * Mirrors dayjs-based presets: lastMonth, lastQuarter, lastFY, ytd, last12.
 */
export function getDateRange(type: DateRangeType): DateRangeResult | null {
  const today = new Date()

  switch (type) {
    case 'lastMonth': {
      const start = startOfMonth(subMonths(today, 1))
      const end = endOfMonth(subMonths(today, 1))
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
        groupBy: 'day',
      }
    }
    case 'lastQuarter': {
      // FY-aligned quarters (Mar–Feb): Q1=Mar–May, Q2=Jun–Aug, Q3=Sep–Nov, Q4=Dec–Feb (matches Xero)
      const m = today.getMonth()
      const y = today.getFullYear()
      let startYear: number
      let startMonth: number
      let endYear: number
      let endMonth: number

      if (m >= 2 && m <= 4) {
        // Current quarter = Q1 (Mar–May) → last quarter = Q4 (Dec–Feb, spanning years)
        startYear = y - 1
        startMonth = 11 // Dec last year
        endYear = y
        endMonth = 1 // Feb this year
      } else if (m >= 5 && m <= 7) {
        // Current quarter = Q2 (Jun–Aug) → last quarter = Q1 (Mar–May)
        startYear = y
        startMonth = 2 // Mar this year
        endYear = y
        endMonth = 4 // May this year
      } else if (m >= 8 && m <= 10) {
        // Current quarter = Q3 (Sep–Nov) → last quarter = Q2 (Jun–Aug)
        startYear = y
        startMonth = 5 // Jun this year
        endYear = y
        endMonth = 7 // Aug this year
      } else {
        // Current quarter = Q4 (Dec–Feb) → last quarter = Q3 (Sep–Nov)
        if (m === 11) {
          // December: last quarter is Sep–Nov same year
          startYear = y
          startMonth = 8 // Sep this year
          endYear = y
          endMonth = 10 // Nov this year
        } else {
          // January/February: last quarter is Sep–Nov previous calendar year
          startYear = y - 1
          startMonth = 8 // Sep last year
          endYear = y - 1
          endMonth = 10 // Nov last year
        }
      }

      const start = new Date(startYear, startMonth, 1)
      const end = endOfMonth(new Date(endYear, endMonth, 1))
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
        groupBy: 'month',
      }
    }
    case 'lastFY': {
      const currentFY = getCurrentFY(today)
      const lastFYStart = new Date(currentFY.start.getFullYear() - 1, currentFY.start.getMonth(), currentFY.start.getDate())
      const lastFYEnd = new Date(currentFY.end.getFullYear() - 1, currentFY.end.getMonth(), currentFY.end.getDate())
      return {
        start: format(lastFYStart, 'yyyy-MM-dd'),
        end: format(lastFYEnd, 'yyyy-MM-dd'),
        groupBy: 'month',
      }
    }
    case 'ytd': {
      const currentFY = getCurrentFY(today)
      const end = today <= currentFY.end ? today : currentFY.end
      return {
        start: format(currentFY.start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
        groupBy: 'month',
      }
    }
    case 'last12': {
      // Last 12 complete calendar months (e.g. Feb 2024 – Jan 2025 when current month is Feb 2025)
      const end = endOfMonth(subMonths(today, 1))
      const start = startOfMonth(subMonths(end, 11))
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
        groupBy: 'month',
      }
    }
    default:
      return null
  }
}
